/* eslint-disable @typescript-eslint/require-await */
import { SavedPlace } from '@ahamove/icons/map';
import { color } from '@ahamove/tokens';
import { getAddressLabels, type Address } from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui';
import {
  ActionIcon,
  Avatar,
  Divider,
  Group,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { ArrowLeft, PlusCircle, X } from 'lucide-react';
import { AddressAutocompleteItem } from '../autocomplete-item';
import { SavedPlaceForm } from '../form';
import { createTranslations, locales } from '../locales';
import type { SavedAddressOptionsProps } from '../types';
import { AutocompleteModalId } from './constants';
import styled from './style.module.css';
import useAutocompleteModal from './use-autocomplete-modal';
import IconToastInfo from '@/assets/icons/toast-info-icon.svg';
import { useGetUserPlace } from '@/components/address-autocomplete/form/use-get-user-place';
import { MAX_NUMBER_OF_USER_PLACE } from '@/lib/constants';
import { genBuildFields } from '@/lib/utils';

export default function AllSavedPlaceModal({
  lang = 'vi',
  pathIndex,
}: SavedAddressOptionsProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const t = createTranslations({ locales, lang });
  const { openModal } = useAutocompleteModal();
  const { status } = useAuth();
  const { data: userPlaces } = useGetUserPlace({
    enabled: status === 'authenticated',
  });

  const { savedLocations: options, updatePathByIndex } = useLocationStore(
    (state) => state
  );

  const handleChangeAddress = async (addr: Address | null) => {
    if (!addr?.lat || !addr?.lng) return;

    const lat = Number(addr.lat);
    const lng = Number(addr.lng);

    const addrData = {
      adr_source: addr.code ? `fav_${addr.code}` : addr.adr_source,
      address: addr.address,
      first_label: addr.first_label,
      second_label: addr.second_label,
      lat,
      lng,
      name: addr.contact_name ?? '',
      mobile: addr.contact_number ?? '',
      remarks: addr.address_detail ?? '',
      level: addr.level,
    };

    const newPath = {
      ...addrData,
    };
    if (pathIndex != null) {
      updatePathByIndex(newPath, pathIndex);
    }
  };

  const handleSelectAddress = (data: Address) => {
    const item = genBuildFields(data, [
      'address',
      'lat',
      'lng',
      'first_label',
      'second_label',
      'address_detail',
      'code',
      'contact_name',
      'contact_number',
      'feature_type',
      'adr_source',
    ]);
    void handleChangeAddress(item);

    modals.close(AutocompleteModalId.ALL_SAVED_PLACED);
  };

  const handleClose = () => {
    modals.close(AutocompleteModalId.ALL_SAVED_PLACED);
  };

  const handleOpenNewSavedPlace = () => {
    if (userPlaces && userPlaces?.length < MAX_NUMBER_OF_USER_PLACE)
      openModal({
        modalId: AutocompleteModalId.NEW_SAVED_PLACED,
        modalContent: <SavedPlaceForm />,
      });
    else {
      toast.info({
        title: 'Bạn đã đạt giới hạn 50 điểm đã lưu',
        withIcon: true,
        withCloseButton: false,
        icon: <IconToastInfo />,
      });
    }
  };

  const isEmpty = !options || options.length < 1;

  return (
    <Stack
      bg={'neutral.0'}
      h={isMobile ? '100svh' : rem(600)}
      style={{ overflow: 'hidden' }}
      gap={rem(12)}
      pb={rem(16)}
    >
      {/* TODO: Modal header  */}
      <Group
        pos={'relative'}
        w="100%"
        px={rem(8)}
        py={0}
        bg="white"
        mih={46}
        style={(theme) => ({
          zIndex: 11,
          border: `1px solid ${theme.colors.neutral[0]}`,
          textAlign: 'center',
        })}
      >
        <Title c="neutral.9" w={'100%'} order={4}>
          {t('save_places')}
        </Title>
        <ActionIcon
          size="lg"
          variant="subtle"
          color="neutral"
          radius="xl"
          style={{
            outline: 'none !important',
            position: 'absolute',
            top: '50%',
            left: isMobile ? rem(8) : 'auto',
            transform: `translateY(-50%)`,
            right: rem(8),
          }}
          onClick={handleClose}
        >
          {isMobile ? (
            <ArrowLeft size={24} color={color.neutral['70']} />
          ) : (
            <X size={24} color={color.neutral['70']} />
          )}
        </ActionIcon>
      </Group>

      <Stack
        h={isMobile ? '100svh' : 'auto'}
        mah={isMobile ? '100svh' : 'auto'}
        style={{ overflow: 'auto' }}
        gap={12}
        flex={1}
      >
        {isEmpty ? null : (
          <Stack bg="white" gap={0}>
            {options.map((o, i) => {
              const [firstLabel] = getAddressLabels(o.address);
              const isLast = options.length - 1;
              return (
                <>
                  <AddressAutocompleteItem
                    key={o.code}
                    primary={o.name ?? firstLabel}
                    secondary={o.address}
                    className={styled['address-item']}
                    px={rem(16)}
                    py={rem(12)}
                    icon={<SavedPlace width={24} height={24} />}
                    onClick={() => handleSelectAddress(o)}
                  />
                  {i !== isLast && <Divider h={1} ml={54} color="neutral.0" />}
                </>
              );
            })}
          </Stack>
        )}

        <Stack bg="white" gap={0}>
          <Group
            gap="md"
            px={rem(16)}
            py={rem(12)}
            wrap="nowrap"
            w="100%"
            mih={isMobile ? rem(65) : rem(57)}
            className={styled['address-item']}
            onClick={handleOpenNewSavedPlace}
          >
            <Avatar size={24} radius="sm" bg={color.primary['60']}>
              <PlusCircle size={16} fill="#fff" stroke={color.primary['60']} />
            </Avatar>
            <Stack gap={2}>
              <Text c="neutral.9" size="sm" fw={600} lineClamp={1}>
                {t('add_new')}
              </Text>
              <Text c="neutral.7" size="xs" lineClamp={1}>
                {t('save_place_sub')}
              </Text>
            </Stack>
          </Group>
        </Stack>
      </Stack>
    </Stack>
  );
}
